import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Snackbar,
  Alert,
  RadioGroup,
  FormControlLabel,
  Radio,
  MenuItem,
  FormHelperText,
  InputLabel,
  Select,
  FormControl
} from '@mui/material';
import axios from 'axios';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import InventoryIcon from '@mui/icons-material/Inventory';
import './productInquiry.css';

// ==================== COUNTRY CODES DATA ====================
let COUNTRY_CODES = [];
let COUNTRY_NAME_TO_CODE_MAP = {};

// ==================== SEARCHABLE SELECT COMPONENT ====================
const SearchableSelect = ({
  value,
  options,
  onChange,
  onOpen,
  placeholder = "Select...",
  label,
  searchPlaceholder = "Search...",
  disabled = false,
  getOptionLabel = (option) => option?.name || '',
  getOptionValue = (option) => option?.id || ''
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState([]);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (searchTerm) {
      const filtered = options.filter(option =>
        getOptionLabel(option).toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOptions(filtered);
    } else {
      setFilteredOptions(options);
    }
  }, [searchTerm, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        handleClose();
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const handleOpen = () => {
    if (disabled) return;
    setOpen(true);
    setSearchTerm('');
    if (onOpen) onOpen();
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  const handleClose = () => {
    setOpen(false);
    setSearchTerm('');
  };

  const handleSelect = (option) => {
    onChange(option);
    handleClose();
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
    setSearchTerm('');
  };

  const selectedLabel = value ? getOptionLabel(value) : '';

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      {label && (
        <label style={{
          display: 'block',
          marginBottom: '6px',
          fontSize: '14px',
          fontWeight: 500,
          color: '#374151'
        }}>
          {label}
        </label>
      )}

      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          type="text"
          value={open ? searchTerm : selectedLabel}
          onChange={(e) => {
            if (open) {
              setSearchTerm(e.target.value);
            }
          }}
          onClick={handleOpen}
          placeholder={open ? searchPlaceholder : placeholder}
          disabled={disabled}
          readOnly={!open}
          style={{
            width: '100%',
            padding: '10px 40px 10px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '14px',
            outline: 'none',
            cursor: open ? 'text' : 'pointer',
            backgroundColor: disabled ? '#f3f4f6' : 'white',
            transition: 'all 0.2s',
            ...(open && {
              borderColor: '#ff3b30',
              boxShadow: '0 0 0 3px rgba(255, 59, 48, 0.1)'
            })
          }}
        />

        {/* Clear button */}
        {value && !open && !disabled && (
          <button
            onClick={handleClear}
            style={{
              position: 'absolute',
              right: '32px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#9ca3af',
              transition: 'color 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = '#ff3b30';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = '#9ca3af';
            }}
            type="button"
          >
            <svg
              style={{
                width: '16px',
                height: '16px',
                pointerEvents: 'none'
              }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}

        {/* Dropdown arrow */}
        <svg
          style={{
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '20px',
            height: '20px',
            color: open ? '#ff3b30' : '#9ca3af',
            pointerEvents: 'none'
          }}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          {open ? (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          )}
        </svg>
      </div>

      {open && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          maxHeight: '400px',
          overflow: 'auto',
          zIndex: 1000,
          marginTop: '4px',
          backgroundColor: 'white',
          borderRadius: '6px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
        }}>
          {filteredOptions.length === 0 ? (
            <div style={{
              padding: '12px 16px',
              color: '#6b7280',
              fontSize: '14px'
            }}>
              No results found
            </div>
          ) : (
            filteredOptions.map((option, index) => {
              const optionValue = getOptionValue(option);
              const isSelected = value && getOptionValue(value) === optionValue;

              return (
                <div
                  key={optionValue || index}
                  onClick={() => handleSelect(option)}
                  style={{
                    padding: '12px 16px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 500,
                    transition: 'all 0.2s',
                    backgroundColor: isSelected ? 'rgba(255, 59, 48, 0.12)' : 'white',
                    color: isSelected ? '#ff3b30' : '#374151',
                    borderBottom: index < filteredOptions.length - 1 ? '1px solid #f3f4f6' : 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 59, 48, 0.08)';
                    e.currentTarget.style.paddingLeft = '20px';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = isSelected ? 'rgba(255, 59, 48, 0.12)' : 'white';
                    e.currentTarget.style.paddingLeft = '16px';
                  }}
                >
                  {getOptionLabel(option)}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

// ==================== COUNTRY CODE SELECT FOR ALL USERS ====================
// selectedId: countryId se exact country (US/Canada dono +1 hain)
const CountryCodeSelect = ({ value, selectedId, onChange, countryName, isLoggedIn = false, options = COUNTRY_CODES }) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (searchTerm) {
      const term = searchTerm.trim().toLowerCase();
      const filtered = options.filter(item =>
        (item.country || '').toLowerCase().includes(term) ||
        (item.code || '').toLowerCase().includes(term) ||
        (item.iso || '').toLowerCase().includes(term)
      );
      // Prioritize country names that start with the search term over those
      // that merely contain it somewhere in the middle (e.g. "in" -> India
      // before Bahrain), keeping the original order within each group.
      // Exact ISO match sabse upar (e.g. "in" -> India, "cl" -> Chile)
      const isoMatch = [];
      const startsWith = [];
      const containsOnly = [];
      filtered.forEach(item => {
        if ((item.iso || '').toLowerCase() === term) {
          isoMatch.push(item);
        } else if ((item.country || '').toLowerCase().startsWith(term)) {
          startsWith.push(item);
        } else {
          containsOnly.push(item);
        }
      });
      setFilteredOptions([...isoMatch, ...startsWith, ...containsOnly]);
    } else {
      setFilteredOptions(options);
    }
  }, [searchTerm, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        handleClose();
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const handleOpen = () => {
    setOpen(true);
    setSearchTerm('');
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  const handleClose = () => {
    setOpen(false);
    setSearchTerm('');
  };

  const handleSelect = (item) => {
    onChange(item.code, item);
    handleClose();
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('', null);
    setSearchTerm('');
  };

  // Pehle countryId se, na mile to dial code se
  const selectedItem =
    (selectedId && options.find(item => item.countryId === selectedId)) ||
    options.find(item => item.code === value);

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          type="text"
          value={open ? searchTerm : (selectedItem ? `${(selectedItem.iso || '').toUpperCase()} ${selectedItem.code}`.trim() : '')}
          onChange={(e) => {
            if (open) {
              setSearchTerm(e.target.value);
            }
          }}
          onClick={handleOpen}
          placeholder="Search country code..."
          readOnly={!open}
          style={{
            width: '100%',
            height: '100%' ,
            padding: '10px 40px 10px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '14px',
            outline: 'none',
            cursor: open ? 'text' : 'pointer',
            backgroundColor: 'white',
            transition: 'all 0.2s',
            ...(open && {
              borderColor: '#ff3b30',
              boxShadow: '0 0 0 3px rgba(255, 59, 48, 0.1)'
            })
          }}
        />

        {/* Clear button */}
        {value && !open && (
          <button
            onClick={handleClear}
            style={{
              position: 'absolute',
              right: '32px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#9ca3af',
              transition: 'color 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = '#ff3b30';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = '#9ca3af';
            }}
            type="button"
          >
            <svg
              style={{
                width: '16px',
                height: '16px',
                pointerEvents: 'none'
              }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}

        {/* Dropdown arrow */}
        <svg
          style={{
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '20px',
            height: '20px',
            color: open ? '#ff3b30' : '#9ca3af',
            pointerEvents: 'none'
          }}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          {open ? (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          )}
        </svg>
      </div>

      {open && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          maxHeight: '400px',
          overflow: 'auto',
          zIndex: 1000,
          marginTop: '4px',
          backgroundColor: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '6px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
        }}>
          {filteredOptions.length === 0 ? (
            <div style={{
              padding: '12px 16px',
              color: '#6b7280',
              fontSize: '14px'
            }}>
              No results found
            </div>
          ) : (
            filteredOptions.map((item, index) => {
              const isSelected = !!selectedItem && item === selectedItem;
              const isCountryMatch = countryName && item.country.toLowerCase().includes(countryName.toLowerCase());

              return (
                <div
                  key={`${item.code}-${index}`}
                  onClick={() => handleSelect(item)}
                  style={{
                    padding: '12px 16px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 500,
                    transition: 'all 0.2s',
                    backgroundColor: isSelected ? 'rgba(255, 59, 48, 0.12)' : (isCountryMatch && !isLoggedIn ? 'rgba(0, 150, 255, 0.08)' : 'white'),
                    color: isSelected ? '#ff3b30' : (isCountryMatch && !isLoggedIn ? 'rgba(226, 109, 41, 1)' : '#374151'),
                    borderBottom: index < filteredOptions.length - 1 ? '1px solid #f3f4f6' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = isSelected ? 'rgba(255, 59, 48, 0.2)' : (isCountryMatch && !isLoggedIn ? 'rgba(0, 150, 255, 0.15)' : 'rgba(255, 59, 48, 0.08)');
                    e.currentTarget.style.paddingLeft = '20px';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = isSelected ? 'rgba(255, 59, 48, 0.12)' : (isCountryMatch && !isLoggedIn ? 'rgba(0, 150, 255, 0.08)' : 'white');
                    e.currentTarget.style.paddingLeft = '16px';
                  }}
                >
                  {/* ISO code capital me (Admin Profile jaisa) - Windows pe flag emoji letters jaise dikhte the */}
                  <span style={{ fontSize: '13px', fontWeight: 600, minWidth: '28px', color: '#111827' }}>{(item.iso || '').toUpperCase()}</span>
                  <span>{item.code}</span>
                  <span style={{ marginLeft: 'auto', color: (isCountryMatch && !isLoggedIn) ? '#c82905ff' : '#6b7280', fontSize: '13px' }}>
                    {item.country}
                    {(isCountryMatch && !isLoggedIn) && ' (Suggested)'}
                    {(isSelected && isLoggedIn) && ' (Your registered code)'}
                  </span>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

// ==================== MAIN PRODUCT INQUIRY COMPONENT ====================
const ProductInquiry = ({ product, imageUrl1, embedded = false, onClose }) => {
  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    emailId: '',
    userMessage: '',
    country: null,
    port: null,
    shippingOption: null,
    shippingCalculation: null,
    shippingType: null,
    containerType: null,
    containerShippingOption: null,
    countryCode: '',
    countryCodeId: null, // selected phone country ka id (US vs Canada)
  });

  // UI state
  const [inquiryType, setInquiryType] = useState('general');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Data state
  const [countriesList, setCountriesList] = useState([]);
  const [portsList, setPortsList] = useState([]);
  const [userDetails, setUserDetails] = useState(null);
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const [countryCodes, setCountryCodes] = useState([]);

  // Constants
  const labelMap = {
    firstName: 'First Name',
    lastName: 'Last Name',
    phoneNumber: 'Phone Number',
    emailId: 'Email Address',
    userMessage: 'Your Message'
  };

  const shippingOptions = [
    {
      value: "FOB",
      label: "FOB (Free On Board)",
      description: "Basic vehicle cost + Japan port handling (you arrange shipping)"
    },
    {
      value: "CNF",
      label: "C&F (Cost and Freight)",
      description: "FOB + shipping cost to your destination port"
    },
    {
      value: "CIF",
      label: "CIF (Cost, Insurance & Freight)",
      description: "C&F + marine insurance"
    }
  ];

  // Fetch country codes from API
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await fetch(
          "https://car.amar.co.jp/api/authorization/shipping/all-countries",
          { headers: { "Accept-Language": "en" } }
        );
        const rawResponse = await res.json();
        // Handle either a bare array response, or one wrapped in an envelope
        // object (e.g. { data: [...] } / { content: [...] } / { countries: [...] }).
        const data = Array.isArray(rawResponse)
          ? rawResponse
          : (rawResponse?.data || rawResponse?.content || rawResponse?.countries || []);
        const isoToFlag = (iso) => {
          if (!iso || typeof iso !== 'string' || iso.length !== 2) return '🏳️';
          return iso.toUpperCase().replace(/./g, (c) =>
            String.fromCodePoint(c.charCodeAt(0) + 127397)
          );
        };
        COUNTRY_CODES = data.map((c) => ({
          code: c.countryPhoneCode,
          country: c.countryName,
          flag: isoToFlag(c.isoCode),
          iso: c.isoCode || '', // ISO se search ke liye (e.g. "IN", "CL") - null-safe
          countryId: c.countryId, // US/Canada dono +1 - exact country id se pehchano
        }));
        // Rebuild COUNTRY_NAME_TO_CODE_MAP from API data
        data.forEach((c) => {
          COUNTRY_NAME_TO_CODE_MAP[c.countryName] = c.countryPhoneCode;
        });
        setCountryCodes([...COUNTRY_CODES]);
      } catch (err) {
        console.error("Failed to fetch countries:", err);
      }
    };
    fetchCountries();
  }, []);

  // Fetch user details when component mounts
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      setIsUserLoggedIn(true);
      fetchUserDetails(token);
    } else {
      setIsUserLoggedIn(false);
    }
  }, []);

  // Match the user's registered country code once BOTH the countries list and the
  // user's details have loaded. fetchCountries() and fetchUserDetails() run in
  // parallel on mount, so whichever finishes last decides when this can run —
  // matching inside fetchUserDetails alone could race ahead of the countries list.
  useEffect(() => {
    if (!userDetails || countryCodes.length === 0) return;
    let userCountryCode = userDetails.countryCode || '';
    if (!userCountryCode) return;
    userCountryCode = userCountryCode.startsWith('+') ? userCountryCode : `+${userCountryCode}`;
    // Profile ka countryId pehle (US vs Canada), na ho to dial code se
    const matchedCode =
      (userDetails.countryId && countryCodes.find(item => item.countryId === userDetails.countryId)) ||
      countryCodes.find(item => item.code === userCountryCode);
    if (matchedCode) {
      setFormData(prev => ({ ...prev, countryCode: matchedCode.code, countryCodeId: matchedCode.countryId || null }));
    }
  }, [userDetails, countryCodes]);

  const fetchUserDetails = async (token) => {
    try {
      const response = await axios.get('https://car.amar.co.jp/api/authorization/user/get/user/details', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.data.code === 200) {
        setUserDetails(response.data.userDetails);

        // Extract just the numbers from user's phone number
        const userPhoneNumber = response.data.userDetails.userMobileNumber || '';
        const phoneDigits = userPhoneNumber.replace(/\D/g, '');

        setFormData(prev => ({
          ...prev,
          firstName: response.data.userDetails.firstName || '',
          lastName: response.data.userDetails.lastName || '',
          phoneNumber: phoneDigits.slice(0, 15) || '', // Get last 10 digits
          emailId: response.data.userDetails.email || '',
          // countryCode is matched separately below, once the country codes list has loaded —
          // fetchCountries() and fetchUserDetails() run in parallel, so matching here
          // immediately can race and fail if this resolves first.
        }));
      }
    } catch (error) {
      console.error('Failed to fetch user details:', error);
      localStorage.removeItem('authToken');
      setUserDetails(null);
      setIsUserLoggedIn(false);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await fetch('https://car.amar.co.jp/api/authorization/shipping/countries');
      const data = await response.json();
      setCountriesList(data);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const fetchPorts = async (countryId) => {
    try {
      const response = await fetch(`https://car.amar.co.jp/api/authorization/shipping/ports/country/${countryId}`);
      const data = await response.json();
      setPortsList(data);
    } catch (error) {
      console.error('Error fetching ports:', error);
    }
  };

  const calculateShippingPrice = async (data) => {
    try {
      const response = await fetch('https://car.amar.co.jp/api/authorization/shipping/calculate-price', {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });

      const responseData = await response.json();

      if (!response.ok) {
        // Handle 400 error with volume exceed limit - THIS IS NOT AN ERROR FOR US
        if (response.status === 400 && responseData.error === 'VOLUME_EXCEEDS_LIMIT') {
          console.log('Volume exceeds limit - returning special response');
          return {
            volumeCubicMeters: responseData.volume || null,
            totalCost: null,
            totalCostIncludingBasePrice: null,
            shippingType: data.shippingType,
            error: 'VOLUME_EXCEEDS_LIMIT',
            errorMessage: responseData.message || 'Volume exceeds maximum allowed limit',
            statusCode: 400
          };
        }
        
        // For other errors, throw
        const error = new Error(responseData.message || 'Failed to calculate shipping price');
        error.response = { status: response.status, data: responseData };
        throw error;
      }

      return responseData;
    } catch (error) {
      console.error('Error calculating shipping price:', error);
      throw error;
    }
  };

  // Validation function - must be exactly 10 digits
  const validateForm = () => {
    if (!formData.countryCode) {
      setErrorMessage('Please select a country code');
      setShowErrorPopup(true);
      return false;
    }

    if (!formData.phoneNumber || formData.phoneNumber.length < 4 || formData.phoneNumber.length > 15) {
      setErrorMessage('Please enter a valid phone number');
      setShowErrorPopup(true);
      return false;
    }
    return true;
  };

  const handleInputChange = (field) => (e) => {
    let value = e.target.value;

    if (field === 'phoneNumber') {
      value = value.replace(/\D/g, '');
      value = value.slice(0, 15);
    }

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCountryCodeChange = (code, item) => {
    setFormData(prev => ({
      ...prev,
      countryCode: code,
      countryCodeId: item?.countryId || null,
      phoneNumber: ''
    }));
  };

  const handleCountryChange = (country) => {
    setFormData(prev => ({
      ...prev,
      country: country,
      port: null,
      shippingOption: null,
      shippingCalculation: null,
      shippingType: null,
      containerType: null,
      containerShippingOption: null
    }));
    setPortsList([]);
    
    if (country) {
      fetchPorts(country.countryId);

      // FOR ALL USERS (both logged in and guests): 
      // Auto-select country code based on selected country
      if (country.countryName && COUNTRY_NAME_TO_CODE_MAP[country.countryName]) {
        const countryCode = COUNTRY_NAME_TO_CODE_MAP[country.countryName];
        // Phone list me wahi country dhundo (pehle id, phir naam se) - taaki US/Canada jaisa mix-up na ho
        const phoneItem =
          countryCodes.find(c => c.countryId === country.countryId) ||
          countryCodes.find(c => c.country === country.countryName);
        // Profile wali hi country chuni to profile ka phone wapas dikhao, warna phone reset
        const isProfileCountry = isUserLoggedIn && !!userDetails && (
          userDetails.countryId
            ? userDetails.countryId === (phoneItem?.countryId ?? country.countryId)
            : (userDetails.countryCode || '').replace(/\D/g, '') === (phoneItem ? phoneItem.code : countryCode).replace(/\D/g, '')
        );
        const profilePhone = (userDetails?.userMobileNumber || '').replace(/\D/g, '').slice(0, 15);
        setFormData(prev => ({
          ...prev,
          countryCode: phoneItem ? phoneItem.code : countryCode,
          countryCodeId: phoneItem?.countryId || null,
          phoneNumber: isProfileCountry ? profilePhone : ''
        }));
      }
    }
  };

  const handlePortChange = (port) => {
    setFormData(prev => ({
      ...prev,
      port: port,
      shippingOption: null,
      shippingCalculation: null,
      shippingType: null,
      containerType: null,
      containerShippingOption: null
    }));
  };

  const handleShippingTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      shippingType: type,
      shippingOption: null,
      containerType: null,
      containerShippingOption: null,
      shippingCalculation: null
    }));
  };

  const handleContainerSelection = (containerType) => {
    setFormData(prev => ({
      ...prev,
      containerType,
      containerShippingOption: null,
      shippingCalculation: null
    }));
  };

  const handleContainerShippingOptionSelection = async (option) => {
    setFormData(prev => ({ ...prev, containerShippingOption: option }));

    if (formData.port && formData.containerType && option) {
      try {
        // Determine shippingType value based on container type
        let shippingTypeValue;
        if (formData.containerType === '20ft') {
          shippingTypeValue = '20ft container';
        } else if (formData.containerType === '40ft') {
          shippingTypeValue = '40ft container';
        } else {
          shippingTypeValue = 'individual shippment'; // fallback
        }

        const payload = {
          carModelId: product?.carModelId || '',
          portId: formData.port?.portId || '',
          shippingOptionType: option,
          isInquiry: false,
          isContainerShipping: true,
          is20ft: formData.containerType === '20ft',
          is40ft: formData.containerType === '40ft',
          firstName: formData.firstName || '',
          lastName: formData.lastName || '',
          mobileNo: formData.phoneNumber,
          emailId: formData.emailId || '',
          userMessage: formData.userMessage || '',
          guestCountryCode: formData.countryCode,
          shippingType: shippingTypeValue
        };
        
        console.log('Calculating container shipping with payload:', payload);
        
        const shippingData = await calculateShippingPrice(payload);

        setFormData(prev => ({
          ...prev,
          shippingCalculation: {
            volumeCubicMeters: shippingData.volumeCubicMeters,
            totalCost: shippingData.totalCost,
            totalCostIncludingBasePrice: shippingData.totalCostIncludingBasePrice,
            containerRate: shippingData.containerRate,
            containerType: shippingData.containerType,
            isContainerShipping: shippingData.isContainerShipping,
            shippingType: shippingData.shippingType,
            error: shippingData.error || null,
            errorMessage: shippingData.errorMessage || null
          }
        }));
      } catch (error) {
        console.error('Error calculating container shipping:', error);
        
        // Check if it's a volume exceed limit error from the catch block
        if (error.response?.status === 400 && error.response?.data?.error === 'VOLUME_EXCEEDS_LIMIT') {
          setFormData(prev => ({
            ...prev,
            shippingCalculation: {
              volumeCubicMeters: error.response.data.volume || null,
              totalCost: null,
              totalCostIncludingBasePrice: null,
              error: 'VOLUME_EXCEEDS_LIMIT',
              errorMessage: error.response.data.message || 'Volume exceeds maximum allowed limit',
              statusCode: 400
            }
          }));
        } else if (error.response?.status === 401) {
          setFormData(prev => ({
            ...prev,
            shippingCalculation: {
              ...prev.shippingCalculation,
              totalCost: null,
              totalCostIncludingBasePrice: null,
              error: 'UNAUTHORIZED'
            }
          }));
        } else if (error.response?.status === 404 || error.message?.includes('Container rate not found')) {
          setFormData(prev => ({
            ...prev,
            shippingCalculation: {
              ...prev.shippingCalculation,
              totalCost: null,
              totalCostIncludingBasePrice: null,
              error: 'CONTAINER_RATE_NOT_FOUND'
            }
          }));
        } else {
          setFormData(prev => ({
            ...prev,
            shippingCalculation: {
              ...prev.shippingCalculation,
              totalCost: null,
              totalCostIncludingBasePrice: null,
              error: 'CALCULATION_ERROR'
            }
          }));
        }
      }
    }
  };

  const handleSubmitInquiry = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('https://car.amar.co.jp/api/dashboard/inquiry/send-inquiry', {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Accept-Language': 'en',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          emailId: formData.emailId,
          phoneNumber: formData.phoneNumber,
          description: formData.userMessage,
          modelId: product?.carModelId,
          guestCountryCode: formData.countryCode
        })
      });

      const data = await response.json();

      if (response.ok) {
        setShowSuccessPopup(true);

        // Reset form to initial state
        resetForm();

        setTimeout(() => {
          if (onClose) {
            onClose();
          }
        }, 2000);
      } else {
        setErrorMessage('Failed to submit inquiry. Please try again.');
        setShowErrorPopup(true);
      }
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      setErrorMessage('Failed to submit inquiry. Please try again.');
      setShowErrorPopup(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitShippingInquiry = async () => {
    if (!validateForm()) {
      return;
    }

    if (!formData.port || (!formData.shippingOption && !formData.containerShippingOption)) {
      setErrorMessage('Please complete all required fields');
      setShowErrorPopup(true);
      return;
    }

    setIsSubmitting(true);

    try {
      const isContainer = formData.shippingType === 'container';

      // Base payload for both individual and container shipping
      const payload = {
        carModelId: product?.carModelId,
        portId: formData.port.portId,
        shippingOptionType: isContainer ? formData.containerShippingOption : formData.shippingOption,
        isInquiry: true, // This tells the API to submit the inquiry
        firstName: formData.firstName,
        lastName: formData.lastName,
        mobileNo: formData.phoneNumber,
        emailId: formData.emailId,
        userMessage: formData.userMessage,
        guestCountryCode: formData.countryCode,
      };

      // Add shipping type specific fields
      if (isContainer) {
        payload.isContainerShipping = true;
        payload.is20ft = formData.containerType === '20ft';
        payload.is40ft = formData.containerType === '40ft';
        payload.shippingType = formData.containerType === '20ft' ? '20ft container' : '40ft container';
      } else {
        payload.isContainerShipping = false;
        payload.is20ft = false;
        payload.is40ft = false;
        payload.shippingType = 'individual shippment';
      }
      
      console.log('Submitting shipping inquiry with payload:', payload);
      
      // Make the API call to submit inquiry
      const response = await fetch('https://car.amar.co.jp/api/authorization/shipping/calculate-price', {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const responseData = await response.json();

      // Check if it's a volume exceed limit response (still a successful inquiry submission)
      if (!response.ok && responseData.error === 'VOLUME_EXCEEDS_LIMIT') {
        console.log('Volume exceeds limit - inquiry submitted successfully with special request');
        
        // Update UI to show volume exceed state
        setFormData(prev => ({
          ...prev,
          shippingCalculation: {
            volumeCubicMeters: responseData.volume || null,
            totalCost: null,
            totalCostIncludingBasePrice: null,
            error: 'VOLUME_EXCEEDS_LIMIT',
            errorMessage: responseData.message || 'Volume exceeds maximum allowed limit',
            statusCode: 400
          }
        }));
        
        // Show success message - inquiry was sent!
        setShowSuccessPopup(true);
        
        // Reset form after successful submission
        setTimeout(() => {
          resetForm();
          if (onClose) {
            onClose();
          }
        }, 2000);
        
        return;
      }
      
      // Handle other errors
      if (!response.ok) {
        throw new Error(responseData.message || 'Failed to submit inquiry');
      }

      // Successful response with calculation
      console.log('Inquiry submitted successfully with calculation:', responseData);
      
      setFormData(prev => ({
        ...prev,
        shippingCalculation: {
          volumeCubicMeters: responseData.volumeCubicMeters,
          totalCost: responseData.totalCost,
          totalCostIncludingBasePrice: responseData.totalCostIncludingBasePrice,
          containerRate: responseData.containerRate,
          containerType: responseData.containerType,
          isContainerShipping: responseData.isContainerShipping,
          shippingType: responseData.shippingType,
          error: null,
          errorMessage: null
        }
      }));
      
      setShowSuccessPopup(true);
      
      // Reset form after successful submission
      setTimeout(() => {
        resetForm();
        if (onClose) {
          onClose();
        }
      }, 2000);

    } catch (error) {
      console.error('Error submitting shipping inquiry:', error);
      
      // Double-check if this is a volume exceed error that wasn't caught above
      if (error.response?.status === 400 && error.response?.data?.error === 'VOLUME_EXCEEDS_LIMIT') {
        console.log('Volume exceeds limit caught in catch - inquiry was still submitted');
        
        setFormData(prev => ({
          ...prev,
          shippingCalculation: {
            volumeCubicMeters: error.response.data.volume || null,
            totalCost: null,
            totalCostIncludingBasePrice: null,
            error: 'VOLUME_EXCEEDS_LIMIT',
            errorMessage: error.response.data.message || 'Volume exceeds maximum allowed limit',
            statusCode: 400
          }
        }));
        
        setShowSuccessPopup(true);
        
        setTimeout(() => {
          resetForm();
          if (onClose) {
            onClose();
          }
        }, 2000);
      } else {
        setErrorMessage('Failed to submit shipping inquiry. Please try again.');
        setShowErrorPopup(true);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    const resetData = {
      firstName: '',
      lastName: '',
      emailId: '',
      phoneNumber: '',
      userMessage: '',
      country: null,
      port: null,
      shippingOption: null,
      shippingCalculation: null,
      shippingType: null,
      containerType: null,
      containerShippingOption: null,
      countryCode: '',
      countryCodeId: null
    };

    if (isUserLoggedIn && userDetails) {
      resetData.firstName = userDetails.firstName || '';
      resetData.lastName = userDetails.lastName || '';
      resetData.emailId = userDetails.email || '';
      resetData.countryCode = formData.countryCode || ''; // Keep the country code for logged in users
      resetData.countryCodeId = formData.countryCodeId || null;
    }

    setFormData(resetData);
  };

  const getSelectedOption = () => {
    return formData.shippingType === 'container' ? formData.containerShippingOption : formData.shippingOption;
  };

  const shouldShowAsk = () => {
    // Show "Quote on Request" for volume exceed limit in all cases
    if (formData.shippingCalculation?.error === 'VOLUME_EXCEEDS_LIMIT') {
      return true;
    }
    
    if (formData.shippingType === 'container') {
      return formData.shippingCalculation?.error === 'CONTAINER_RATE_NOT_FOUND' ||
        formData.shippingCalculation?.error === 'UNAUTHORIZED';
    } else {
      return (formData.shippingOption === "CIF" || formData.shippingOption === "CNF") &&
        (!product?.carLength || !product?.carWidth || !product?.carHeight);
    }
  };

  const getShippingTypeDisplay = () => {
    // Check from form data first
    if (formData.shippingType === 'individual') {
      return 'Individual Shipment (RORO)';
    } else if (formData.shippingType === 'container') {
      return formData.containerType === '20ft' ? '20ft Container' : '40ft Container';
    }

    // Check from calculation response if available
    if (formData.shippingCalculation?.shippingType) {
      const shippingType = formData.shippingCalculation.shippingType.toLowerCase();
      if (shippingType.includes('individual')) {
        return 'Individual Shipment (RORO)';
      } else if (shippingType.includes('20ft')) {
        return '20ft Container';
      } else if (shippingType.includes('40ft')) {
        return '40ft Container';
      } else if (shippingType === '20') {
        return '20ft Container';
      } else if (shippingType === '40') {
        return '40ft Container';
      }
      return shippingType;
    }

    return '';
  };

  const renderPhoneNumberField = () => {
    const selectedCountryName = formData.country?.countryName || '';
    const hasPhoneError = formData.phoneNumber && formData.phoneNumber.length !== 10;

    return (
      <Grid item xs={12} md={6}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {/* ALL users can change country code - use the same component for everyone */}
              <CountryCodeSelect
                value={formData.countryCode || ''}
                selectedId={formData.countryCodeId}
                onChange={handleCountryCodeChange}
                countryName={selectedCountryName}
                isLoggedIn={isUserLoggedIn}
                options={countryCodes}
              />

              {!formData.countryCode && (
                <FormHelperText sx={{ color: '#f44336', m: 0, mt: 0.5 }}>
                  Country code is required
                </FormHelperText>
              )}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <TextField
                fullWidth
                size="small"
                id="phoneNumber"
                placeholder="Enter your phone number"
                label="Phone Number"
                value={formData.phoneNumber || ""}
                onChange={handleInputChange('phoneNumber')}
                required
                variant="outlined"
                error={hasPhoneError}
                FormHelperTextProps={{
                  sx: { color: '#f44336', }
                }}
                inputProps={{
                  maxLength: 15,
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: hasPhoneError ? "#f44336" : "#ff3b30",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: hasPhoneError ? "#f44336" : "rgba(0, 0, 0, 0.6)",
                    "&.Mui-focused": {
                      color: hasPhoneError ? "#f44336" : "#ff3b30",
                    },
                  },
                }}
              />
            </Box>
          </Box>

          {isUserLoggedIn && userDetails && (
            <Typography variant="caption" color="#ff3b30" sx={{ mt: 0.5, fontStyle: 'italic' }}>
              Using your registered information. You can update the country code if needed.
            </Typography>
          )}

          {!isUserLoggedIn && selectedCountryName && (
            <Typography variant="caption" color="#d05015ff" sx={{ mt: 0.5, fontStyle: 'italic' }}>
              Country code suggested based on your selected country: {selectedCountryName}
            </Typography>
          )}
        </Box>
      </Grid>
    );
  };

  return (
    <>
      <Container maxWidth="lg" sx={{ px: 0, '@media (min-width: 600px)': { px: 0 } }}>
        <Grid container>
          <Grid item xs={12}>
            <Card className="inquiry-card">
              <CardContent>
                <Typography variant="h5" className="inquiry-title">
                  Vehicle Inquiry Form
                </Typography>

                <Grid container spacing={6} alignItems="center" justifyContent="center" sx={{ mb: 3 }}>
                  <Grid item xs={12} md="auto">
                    <Typography
                      variant="h8"
                      fontWeight="600"
                      sx={{ mt: 2, mb: 2, display: "flex", justifyContent: "center", backgroundColor: "lightyellow" }}
                    >
                      Make Sure All * Required Fields Are Properly Filled.
                    </Typography>

                    <Box className="inquiry-type-buttons">
                      <Button
                        variant={inquiryType === 'general' ? 'contained' : 'outlined'}
                        onClick={() => setInquiryType('general')}
                        className={`inquiry-type-btn ${inquiryType === 'general' ? 'active' : ''}`}
                      >
                        General Inquiry
                      </Button>

                      <Button
                        variant={inquiryType === 'shipping' ? 'contained' : 'outlined'}
                        onClick={() => setInquiryType('shipping')}
                        className={`inquiry-type-btn ${inquiryType === 'shipping' ? 'active' : ''}`}
                      >
                        Shipping Quote
                      </Button>
                    </Box>
                  </Grid>

                  {embedded && formData.country && formData.port && getSelectedOption() && (
                    <Grid item xs={12} md="auto">
                      <Box className="cost-summary-fixed">
                        <Typography variant="body2" className="cost-label">
                          {formData.shippingType === 'container'
                            ? `${formData.containerType === '20ft' ? '20ft' : '40ft'} Container ${formData.containerShippingOption === 'CNF' ? 'C&F' : formData.containerShippingOption || ''} Cost`
                            : formData.shippingOption === "CIF"
                              ? "Total CIF Cost"
                              : formData.shippingOption === "CNF"
                                ? "Total C&F Cost"
                                : formData.shippingOption === "FOB"
                                  ? "Total FOB Cost"
                                  : "Total Cost"}
                        </Typography>
                        <Typography variant="h4" className="cost-value">
                          {shouldShowAsk()
                            ? 'Quote on Request'
                            : formData.shippingCalculation?.totalCostIncludingBasePrice
                              ? `$${formData.shippingCalculation.totalCostIncludingBasePrice.toFixed(2)}`
                              : (formData.shippingType === 'container' ? 'Quote on Request' : `$${product?.carPrice?.toFixed(2) || "0.00"}`)}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>

                {inquiryType === 'general' && (
                  <Box className="form-section">
                    <Typography variant="h6" className="section-title">
                      <MailOutlineIcon fontSize="small" /> General Inquiry Form
                    </Typography>

                    <Grid container spacing={2}>
                      {['firstName', 'lastName'].map((field) => (
                        <Grid item xs={12} md={6} key={field}>
                          <TextField
                            fullWidth
                            size="small"
                            id={field}
                            label={labelMap[field]}
                            value={formData[field] || ""}
                            onChange={handleInputChange(field)}
                            required
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                            className="custom-textfield"
                          />
                        </Grid>
                      ))}

                      {renderPhoneNumberField()}

                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="small"
                          id="emailId"
                          label="Email Address"
                          value={formData.emailId || ""}
                          onChange={handleInputChange('emailId')}
                          required
                          variant="outlined"
                          InputLabelProps={{ shrink: true }}
                          className="custom-textfield"
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          multiline
                          minRows={3}
                          variant="outlined"
                          id="userMessage"
                          size="small"
                          label="Your Message"
                          value={formData.userMessage}
                          InputLabelProps={{ shrink: true }}
                          onChange={handleInputChange('userMessage')}
                          className="custom-textfield"
                        />
                      </Grid>

                      <Grid item xs={12} sx={{ mt: 1 }}>
                        <Button
                          fullWidth
                          variant="contained"
                          size="medium"
                          onClick={handleSubmitInquiry}
                          disabled={
                            !formData.firstName ||
                            !formData.lastName ||
                            !formData.phoneNumber ||
                            !formData.countryCode ||
                            formData.phoneNumber.length < 4 || formData.phoneNumber.length > 15 ||
                            !formData.emailId ||
                            isSubmitting
                          }
                          className="submit-button"
                        >
                          {isSubmitting ? (
                            <>
                              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                              Submitting...
                            </>
                          ) : (
                            'Submit Inquiry'
                          )}
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {inquiryType === 'shipping' && (
                  <Box>
                    <Box className={`step-box ${formData.country ? 'completed' : 'pending'}`}>
                      <Typography variant="subtitle1" className="step-title-product-inquiry">
                        {formData.country ? <CheckCircleOutlineIcon fontSize="small" /> : <LocationOnIcon fontSize="small" />}
                        {formData.country ? 'Country Selected' : 'Step 1: Select Destination'}
                      </Typography>

                      <SearchableSelect
                        value={formData.country}
                        options={countriesList}
                        onChange={handleCountryChange}
                        onOpen={() => {
                          if (countriesList.length === 0) fetchCountries();
                        }}
                        placeholder="Click to search countries..."
                        searchPlaceholder="Type to search countries..."
                        getOptionLabel={(option) => option?.countryName || ''}
                        getOptionValue={(option) => option?.countryId || ''}
                      />
                    </Box>

                    {formData.country && (
                      <Box className={`step-box ${formData.port ? 'completed' : 'pending'}`}>
                        <Typography variant="subtitle1" className="step-title-product-inquiry">
                          {formData.port ? <CheckCircleOutlineIcon fontSize="small" /> : <DirectionsBoatIcon fontSize="small" />}
                          {formData.port ? 'Port Selected' : 'Step 2: Select Port'}
                        </Typography>

                        <SearchableSelect
                          value={formData.port}
                          options={portsList}
                          onChange={handlePortChange}
                          placeholder="Click to search ports..."
                          searchPlaceholder="Type to search ports..."
                          disabled={!formData.country}
                          getOptionLabel={(option) => option?.portName || ''}
                          getOptionValue={(option) => option?.portId || ''}
                        />
                      </Box>
                    )}

                    {formData.port && (
                      <Box className={`step-box ${formData.shippingType ? 'completed' : 'pending'}`}>
                        <Typography variant="subtitle1" className="step-title-product-inquiry">
                          {formData.shippingType ? <CheckCircleOutlineIcon fontSize="small" /> : <LocalShippingIcon fontSize="small" />}
                          {formData.shippingType ? 'Shipping Type Selected' : 'Step 3: Select Shipping Type'}
                        </Typography>

                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Box
                              onClick={() => handleShippingTypeChange('individual')}
                              className={`shipping-type-card ${formData.shippingType === 'individual' ? 'selected' : ''}`}
                            >
                              <LocalShippingIcon sx={{ fontSize: 40, mb: 1 }} />
                              <Typography variant="h6" fontWeight="700" fontSize="1rem">
                                Individual Shipment (RORO)
                              </Typography>
                              <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                                FOB, C&F, CIF Options
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <Box
                              onClick={() => handleShippingTypeChange('container')}
                              className={`shipping-type-card ${formData.shippingType === 'container' ? 'selected' : ''}`}
                            >
                              <InventoryIcon sx={{ fontSize: 40, mb: 1 }} />
                              <Typography variant="h6" fontWeight="700" fontSize="1rem">
                                Container Shipment
                              </Typography>
                              <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                                20ft & 40ft Containers
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    )}

                    {formData.shippingType === 'individual' && (
                      <Box className={`step-box ${formData.shippingOption ? 'completed' : 'pending'}`}>
                        <Typography variant="subtitle1" className="step-title-product-inquiry">
                          {formData.shippingOption ? <CheckCircleOutlineIcon fontSize="small" /> : <LocalShippingIcon fontSize="small" />}
                          {formData.shippingOption ? 'Shipping Option Selected' : 'Step 4: Select Shipping Option'}
                        </Typography>

                        <RadioGroup
                          value={formData.shippingOption || ''}
                          onChange={async (e) => {
                            const newShippingOption = e.target.value;
                            const updatedFormData = {
                              ...formData,
                              shippingOption: newShippingOption
                            };

                            setFormData(updatedFormData);

                            if (updatedFormData.port && newShippingOption) {
                              try {
                                const shippingData = await calculateShippingPrice({
                                  carModelId: product?.carModelId || '',
                                  portId: updatedFormData.port?.portId || '',
                                  shippingOptionType: newShippingOption,
                                  isInquiry: false,
                                  isContainerShipping: false,
                                  is20ft: false,
                                  is40ft: false,
                                  firstName: updatedFormData.firstName || '',
                                  lastName: updatedFormData.lastName || '',
                                  mobileNo: updatedFormData.phoneNumber,
                                  emailId: updatedFormData.emailId || '',
                                  userMessage: updatedFormData.userMessage || '',
                                  guestCountryCode: updatedFormData.countryCode,
                                  shippingType: 'individual shippment'
                                });

                                setFormData(prev => ({
                                  ...prev,
                                  shippingCalculation: {
                                    volumeCubicMeters: shippingData.volumeCubicMeters,
                                    totalCost: shippingData.totalCost,
                                    totalCostIncludingBasePrice: shippingData.totalCostIncludingBasePrice,
                                    shippingType: shippingData.shippingType,
                                    error: shippingData.error || null,
                                    errorMessage: shippingData.errorMessage || null
                                  }
                                }));
                              } catch (error) {
                                console.error('Error calculating shipping:', error);
                                if (error.response?.status === 401) {
                                  setFormData(prev => ({
                                    ...prev,
                                    shippingCalculation: {
                                      ...prev.shippingCalculation,
                                      totalCost: null,
                                      totalCostIncludingBasePrice: null,
                                      error: 'UNAUTHORIZED'
                                    }
                                  }));
                                }
                              }
                            }
                          }}
                          sx={{ display: 'flex', gap: 1, flexDirection: 'row', flexWrap: 'wrap' }}
                        >
                          {shippingOptions.map((option) => (
                            <FormControlLabel
                              key={option.value}
                              value={option.value}
                              control={
                                <Radio
                                  size="small"
                                  sx={{
                                    color: '#ff3b30',
                                    '&.Mui-checked': { color: '#ff3b30' },
                                    padding: '6px'
                                  }}
                                />
                              }
                              label={
                                <Box className={`shipping-option-label ${formData.shippingOption === option.value ? 'selected' : ''}`}>
                                  <Typography fontWeight="600" fontSize="0.875rem">
                                    {option.label.split(' (')[0]}
                                  </Typography>
                                  <Typography fontSize="0.7rem" color="text.secondary" sx={{ mb: 0.5 }}>
                                    {option.label.includes('(') ? `(${option.label.split(' (')[1]}` : ''}
                                  </Typography>
                                  <Typography fontSize="0.65rem" color="text.secondary" sx={{ fontStyle: 'italic', lineHeight: 1.3 }}>
                                    {option.description}
                                  </Typography>
                                </Box>
                              }
                              sx={{
                                m: 0,
                                flex: 1,
                                minWidth: '150px',
                                '& .MuiFormControlLabel-label': {
                                  flex: 1,
                                  marginLeft: '4px'
                                }
                              }}
                            />
                          ))}
                        </RadioGroup>
                      </Box>
                    )}

                    {formData.shippingType === 'container' && (
                      <Box className={`step-box ${formData.containerType ? 'completed' : 'pending'}`}>
                        <Typography variant="subtitle1" className="step-title-product-inquiry">
                          {formData.containerType ? <CheckCircleOutlineIcon fontSize="small" /> : <InventoryIcon fontSize="small" />}
                          {formData.containerType ? 'Container Selected' : 'Step 4: Select Container Size'}
                        </Typography>

                        <RadioGroup
                          value={formData.containerType || ''}
                          onChange={(e) => handleContainerSelection(e.target.value)}
                          sx={{ display: 'flex', gap: 1, flexDirection: 'row', flexWrap: 'wrap' }}
                        >
                          <FormControlLabel
                            value="20ft"
                            control={
                              <Radio
                                size="small"
                                sx={{
                                  color: '#ff3b30',
                                  '&.Mui-checked': { color: '#ff3b30' },
                                  padding: '6px'
                                }}
                              />
                            }
                            label={
                              <Box className={`shipping-option-label ${formData.containerType === '20ft' ? 'selected' : ''}`}>
                                <Typography fontWeight="600" fontSize="0.875rem">
                                  20ft Container
                                </Typography>
                                <Typography fontSize="0.75rem" color="text.secondary">
                                  5.9m × 2.35m × 2.39m
                                </Typography>
                              </Box>
                            }
                            sx={{
                              m: 0,
                              flex: 1,
                              minWidth: '150px',
                              '& .MuiFormControlLabel-label': {
                                flex: 1,
                                marginLeft: '4px'
                              }
                            }}
                          />
                          <FormControlLabel
                            value="40ft"
                            control={
                              <Radio
                                size="small"
                                sx={{
                                  color: '#ff3b30',
                                  '&.Mui-checked': { color: '#ff3b30' },
                                  padding: '6px'
                                }}
                              />
                            }
                            label={
                              <Box className={`shipping-option-label ${formData.containerType === '40ft' ? 'selected' : ''}`}>
                                <Typography fontWeight="600" fontSize="0.875rem">
                                  40ft Container
                                </Typography>
                                <Typography fontSize="0.75rem" color="text.secondary">
                                  12m × 2.35m × 2.39m
                                </Typography>
                              </Box>
                            }
                            sx={{
                              m: 0,
                              flex: 1,
                              minWidth: '150px',
                              '& .MuiFormControlLabel-label': {
                                flex: 1,
                                marginLeft: '4px'
                              }
                            }}
                          />
                        </RadioGroup>
                      </Box>
                    )}

                    {formData.containerType && (
                      <Box className={`step-box ${formData.containerShippingOption ? 'completed' : 'pending'}`}>
                        <Typography variant="subtitle1" className="step-title-product-inquiry">
                          {formData.containerShippingOption ? <CheckCircleOutlineIcon fontSize="small" /> : <LocalShippingIcon fontSize="small" />}
                          {formData.containerShippingOption ? 'Shipping Option Selected' : 'Step 5: Select Shipping Option'}
                        </Typography>

                        <RadioGroup
                          value={formData.containerShippingOption || ''}
                          onChange={(e) => handleContainerShippingOptionSelection(e.target.value)}
                          sx={{ display: 'flex', gap: 1, flexDirection: 'row', flexWrap: 'wrap' }}
                        >
                          {shippingOptions.map((option) => (
                            <FormControlLabel
                              key={option.value}
                              value={option.value}
                              control={
                                <Radio
                                  size="small"
                                  sx={{
                                    color: '#ff3b30',
                                    '&.Mui-checked': { color: '#ff3b30' },
                                    padding: '6px'
                                  }}
                                />
                              }
                              label={
                                <Box className={`shipping-option-label ${formData.containerShippingOption === option.value ? 'selected' : ''}`}>
                                  <Typography fontWeight="600" fontSize="0.875rem">
                                    {option.label.split(' (')[0]}
                                  </Typography>
                                  <Typography fontSize="0.7rem" color="text.secondary" sx={{ mb: 0.5 }}>
                                    {option.label.includes('(') ? `(${option.label.split(' (')[1]}` : ''}
                                  </Typography>
                                  <Typography fontSize="0.65rem" color="text.secondary" sx={{ fontStyle: 'italic', lineHeight: 1.3 }}>
                                    {option.description}
                                  </Typography>
                                </Box>
                              }
                              sx={{
                                m: 0,
                                flex: 1,
                                minWidth: '150px',
                                '& .MuiFormControlLabel-label': {
                                  flex: 1,
                                  marginLeft: '4px'
                                }
                              }}
                            />
                          ))}
                        </RadioGroup>
                      </Box>
                    )}

                    {(formData.shippingOption || formData.containerShippingOption) && (
                      <Box className="cost-summary-section">
                        <Typography variant="subtitle1" className="section-title">
                          <AttachMoneyIcon fontSize="small" /> COST SUMMARY
                        </Typography>

                        <Grid container spacing={1} sx={{ mb: 2 }}>
                          <Grid item xs={12} md={4}>
                            <Box className="cost-box">
                              <Typography variant="body2" className="cost-box-label">
                                {formData.shippingType === 'container' ? 'Container Type' : 'Vehicle Price'}
                              </Typography>
                              <Typography variant="h6" className="cost-box-value">
                                {formData.shippingType === 'container'
                                  ? (formData.containerType === '20ft' ? '20ft Container' : '40ft Container')
                                  : `$${product?.carPrice?.toFixed(2) || '0.00'}`}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Box className="cost-box">
                              <Typography variant="body2" className="cost-box-label">
                                Shipping Cost
                              </Typography>
                              <Typography variant="h6" className="cost-box-value">
                                {formData.shippingType === 'container' ? (
                                  (formData.shippingCalculation?.error === 'CONTAINER_RATE_NOT_FOUND' ||
                                    formData.shippingCalculation?.error === 'UNAUTHORIZED' ||
                                    formData.shippingCalculation?.error === 'VOLUME_EXCEEDS_LIMIT')
                                    ? 'Quote on Request'
                                    : formData.shippingCalculation?.totalCost ? `$${formData.shippingCalculation.totalCost.toFixed(2)}`
                                      : 'Quote on Request'
                                ) : (
                                  (formData.shippingOption === "CIF" || formData.shippingOption === "CNF") &&
                                    (!product?.carLength || !product?.carWidth || !product?.carHeight)
                                    ? 'Quote on Request'
                                    : formData.shippingCalculation?.error === 'UNAUTHORIZED' || formData.shippingCalculation?.error === 'VOLUME_EXCEEDS_LIMIT'
                                      ? 'Quote on Request'
                                      : (formData.shippingCalculation?.totalCost
                                        ? `$${formData.shippingCalculation.totalCost.toFixed(2)}`
                                        : '$0.00')
                                )}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Box className="cost-box">
                              <Typography variant="body2" className="cost-box-label">
                                Total Cost
                              </Typography>
                              <Typography variant="h6" className="cost-box-value">
                                {formData.shippingType === 'container' ? (
                                  (formData.shippingCalculation?.error === 'CONTAINER_RATE_NOT_FOUND' ||
                                    formData.shippingCalculation?.error === 'UNAUTHORIZED' ||
                                    formData.shippingCalculation?.error === 'VOLUME_EXCEEDS_LIMIT')
                                    ? 'Quote on Request'
                                    : formData.shippingCalculation?.totalCostIncludingBasePrice
                                      ? `$${formData.shippingCalculation.totalCostIncludingBasePrice.toFixed(2)}`
                                      : 'Quote on Request'
                                ) : (
                                  (formData.shippingOption === "CIF" || formData.shippingOption === "CNF") &&
                                    (!product?.carLength || !product?.carWidth || !product?.carHeight)
                                    ? 'Quote on Request'
                                    : formData.shippingCalculation?.error === 'UNAUTHORIZED' || formData.shippingCalculation?.error === 'VOLUME_EXCEEDS_LIMIT'
                                      ? 'Quote on Request'
                                      : (formData.shippingCalculation?.totalCostIncludingBasePrice
                                        ? `$${formData.shippingCalculation.totalCostIncludingBasePrice.toFixed(2)}`
                                        : `$${product?.carPrice?.toFixed(2) || '0.00'}`)
                                )}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>

                        <Typography variant="caption" color="#faf1f1ff" sx={{ fontStyle: 'normal', fontSize: '12px', fontWeight: 'bold', backgroundColor: 'red', borderRadius: '6px', padding: '8px' }}>
                          {formData.shippingCalculation?.error === 'VOLUME_EXCEEDS_LIMIT' 
                            ? '* Vehicle volume exceeds standard shipping limits. Please contact us for special arrangements.'
                            : formData.shippingType === 'container'
                              ? '* Container pricing includes shipping to destination port. Contact us for final quote.'
                              : '* Final shipping costs will be confirmed after vehicle inspection'}
                        </Typography>
                      </Box>
                    )}

                    {(formData.shippingOption || formData.containerShippingOption) && (
                      <Box className="form-section">
                        <Typography variant="subtitle1" className="section-title">
                          <PersonOutlineIcon fontSize="small" /> Contact Information
                        </Typography>

                        <Grid container spacing={2}>
                          {['firstName', 'lastName'].map((field) => (
                            <Grid item xs={12} md={6} key={field}>
                              <TextField
                                fullWidth
                                size="small"
                                id={field}
                                label={labelMap[field]}
                                value={formData[field] || ""}
                                onChange={handleInputChange(field)}
                                required
                                variant="outlined"
                                InputLabelProps={{ shrink: true }}
                                className="custom-textfield"
                              />
                            </Grid>
                          ))}

                          {renderPhoneNumberField()}

                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              size="small"
                              id="emailId"
                              label="Email Address"
                              value={formData.emailId || ""}
                              onChange={handleInputChange('emailId')}
                              required
                              variant="outlined"
                              InputLabelProps={{ shrink: true }}
                              className="custom-textfield"
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              multiline
                              minRows={3}
                              size="small"
                              label="Your Message"
                              value={formData.userMessage}
                              onChange={handleInputChange('userMessage')}
                              InputLabelProps={{ shrink: true }}
                              variant="outlined"
                              className="custom-textfield"
                            />
                          </Grid>

                          <Grid item xs={12} sx={{ mt: 1 }}>
                            <Button
                              fullWidth
                              variant="contained"
                              size="medium"
                              onClick={handleSubmitShippingInquiry}
                              disabled={
                                !formData.firstName ||
                                !formData.lastName ||
                                !formData.phoneNumber ||
                                !formData.countryCode ||
                                formData.phoneNumber.length < 4 || formData.phoneNumber.length > 15 ||
                                !formData.emailId ||
                                !formData.port ||
                                (!formData.shippingOption && !formData.containerShippingOption) ||
                                isSubmitting
                              }
                              className="submit-button"
                            >
                              {isSubmitting ? (
                                <>
                                  <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                                  Processing...
                                </>
                              ) : (
                                'Get Shipping Quote'
                              )}
                            </Button>
                          </Grid>
                        </Grid>
                      </Box>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      <Snackbar
        open={showSuccessPopup}
        autoHideDuration={2000}
        onClose={() => setShowSuccessPopup(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSuccessPopup(false)}
          severity="success"
          sx={{ width: '100%', backgroundColor: '#4caf50', color: 'white' }}
        >
          Inquiry sent successfully!
        </Alert>
      </Snackbar>

      <Snackbar
        open={showErrorPopup}
        autoHideDuration={6000}
        onClose={() => setShowErrorPopup(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowErrorPopup(false)}
          severity="error"
          sx={{ width: '100%', backgroundColor: '#ff3b30', color: 'white' }}
        >
          {errorMessage || 'Failed to submit inquiry. Please try again.'}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ProductInquiry;